// icons/registry.tsx
import * as React from 'react';
import { IconProps, IconToken, normalizeToken, sizeToPx } from './types';
import { LUCIDE_MAP } from './providers/lucide';
import { CUSTOM_MAP } from './providers/custom';
import { ALIASES } from './aliases';

type Provider = 'custom' | 'lucide';

type RegistryEntry = {
  provider: Provider;
  token: IconToken;
};

const REGISTRY: Partial<Record<IconToken, RegistryEntry>> = {
  magnet:       { provider: 'lucide', token: 'magnet' },
  accessibility:{ provider: 'lucide', token: 'accessibility' },
  info:         { provider: 'lucide', token: 'info' },
  error:        { provider: 'lucide', token: 'error' },
  check:        { provider: 'lucide', token: 'check' },
  whatsapp:     { provider: 'custom', token: 'whatsapp' },
  rotateccw:    { provider: 'lucide', token: 'rotateccw' },
  waves:        { provider: 'lucide', token: 'waves' },
  snowflake:    { provider: 'lucide', token: 'snowflake' },
  flame:        { provider: 'lucide', token: 'flame' },
  boxes:        { provider: 'lucide', token: 'boxes' },
  checkcircle2: { provider: 'lucide', token: 'checkcircle2' },
  clock:       { provider: 'lucide', token: 'clock' },
  playcircle:  { provider: 'lucide', token: 'playcircle' },
  checkcheck:  { provider: 'lucide', token: 'checkcheck' },
  xcircle:     { provider: 'lucide', token: 'xcircle' },
  default:      { provider: 'lucide', token: 'info' }, // fallback visual
};

function resolveToken(rawName: string): RegistryEntry {
  const name = normalizeToken(rawName);

  // 1) directo
  const direct = REGISTRY[name];
  if (direct) return direct;

  // 2) alias
  const aliased = ALIASES[name];
  if (aliased && REGISTRY[aliased]) return REGISTRY[aliased]!;

  // 3) fallback
  return REGISTRY.default!;
}

function renderFromProvider(entry: RegistryEntry, props: React.SVGProps<SVGSVGElement>) {
  const { provider, token } = entry;

  if (provider === 'custom') {
    const C = CUSTOM_MAP[token];
    if (C) return <C {...props} />;
  }

  const L = LUCIDE_MAP[token];
  if (L) return <L {...props} />;

  // último fallback
  const Fallback = LUCIDE_MAP.info!;
  return <Fallback {...props} />;
}

export function Icon({ name, size = 'md', title, className, ...rest }: IconProps) {
  const entry = resolveToken(String(name));
  const px = sizeToPx(size);

  // a11y
  const ariaProps = title
    ? { role: 'img' as const, 'aria-label': title }
    : { 'aria-hidden': true as const };

  return renderFromProvider(entry, {
    width: px,
    height: px,
    className,
    color: 'currentColor',
    focusable: false,
    ...ariaProps,
    ...rest,
  });
}
